import { useSyncExternalStore } from "react";

/**
 * Admin layout breakpoints (T11-01): ≥ 1280 = full sidebar; 768–1279 = icon rail + bottom nav;
 * < 768 = bottom nav + thin header. Decided in JS (not only CSS) so each navigation exists once in
 * the DOM for assistive tech. Without `matchMedia` (tests, SSR) the desktop layout is used.
 */
export type Viewport = "desktop" | "tablet" | "phone";

const DESKTOP = "(min-width: 1280px)";
const TABLET = "(min-width: 768px)";

function query(media: string): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(media)
    : null;
}

function subscribe(onChange: () => void): () => void {
  const lists = [query(DESKTOP), query(TABLET)].filter((l): l is MediaQueryList => l !== null);
  for (const list of lists) {
    list.addEventListener("change", onChange);
  }
  return () => {
    for (const list of lists) {
      list.removeEventListener("change", onChange);
    }
  };
}

export function currentViewport(): Viewport {
  const desktop = query(DESKTOP);
  if (!desktop) {
    return "desktop";
  }
  if (desktop.matches) {
    return "desktop";
  }
  return query(TABLET)?.matches ? "tablet" : "phone";
}

export function useViewport(): Viewport {
  return useSyncExternalStore(subscribe, currentViewport, () => "desktop");
}
